import { clearActiveAuthMode, readActiveAuthMode, writeActiveAuthMode } from "@/features/auth/shared/authModeStorage";
import { logoutSession } from "@/lib/authApi";

const SESSION_KEY = "shopbook.auth.session.independent";
const LEGACY_SESSION_KEY = "shopbook.session";
const AUTH_MODE = "independent";
const SESSION_SOURCE = "web_direct";

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in private/incognito contexts.
  }
}

function removeStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures in private/incognito contexts.
  }
}

export function readIndependentSession() {
  try {
    const raw = readStorage(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function normalizeIndependentSession(session) {
  if (!session) {
    return null;
  }

  return {
    ...session,
    authMode: AUTH_MODE,
    sessionSource: session.sessionSource || SESSION_SOURCE,
    lastAuthenticatedAt: session.lastAuthenticatedAt || new Date().toISOString(),
  };
}

export function readLegacySession() {
  try {
    const raw = readStorage(LEGACY_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveIndependentSession(session) {
  const normalized = normalizeIndependentSession(session);
  if (!normalized) {
    return null;
  }

  writeStorage(SESSION_KEY, JSON.stringify(normalized));
  writeActiveAuthMode(AUTH_MODE);
  return normalized;
}

export async function revokeIndependentSession(sessionId) {
  if (!sessionId) {
    return;
  }

  await logoutSession(sessionId);
}

export function clearIndependentSession() {
  removeStorage(SESSION_KEY);
  // Remove the pre-independent-auth record as well. Older installations can
  // contain both keys; leaving the legacy value would allow a stale session to
  // be rehydrated after an expired-session logout and page reload.
  removeStorage(LEGACY_SESSION_KEY);
  if (readActiveAuthMode() === AUTH_MODE) {
    clearActiveAuthMode();
  }
}

export function hydrateIndependentSessionFromLegacy(session) {
  const normalized = normalizeIndependentSession(session);
  if (!normalized) {
    return null;
  }

  saveIndependentSession(normalized);
  removeStorage(LEGACY_SESSION_KEY);
  return normalized;
}
